// Visionneuse de plan pour la Proposition TRAME : fond (PDF ou image, FondPlan) + un calque SVG fourni par l'appelant.
// Déplacement au doigt (défilement natif), pincement à deux doigts pour zoomer, Ctrl + molette, boutons de zoom ; centrage
// demandé de l'extérieur (point de contrôle cliqué) ; en mode tracé, clic / toucher pour poser un point, flèches + Entrée au clavier.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import { FondPlan } from '../FondPlan.tsx';
import { Bouton } from '../../../components/ui.tsx';
import type { PlanVue } from '../modele.ts';
import type { Pt } from '@shared/proposition/types.ts';

export interface PlanCalquePoignee { zoomer: (f: number) => void; ajuster: () => void }
interface Props {
  plan: PlanVue;
  /** Contenu SVG ; u = une unité d'écran exprimée en unités du plan (traits et textes à taille constante). */
  calque: (u: number) => ReactNode;
  /** Mode tracé : un clic (ou Entrée) pose un point. */
  poser?: (p: Pt, u: number) => void;
  /** Centrage demandé (le jeton change à chaque demande). */
  cible?: { x: number; y: number; jeton: number } | null;
  libelle: string;
  /** Contenu HTML posé au-dessus du plan (étiquette permanente), hors défilement. */
  surimpression?: ReactNode;
  compact?: boolean;
  /** Zone utile du plan (pièces, éléments) : « Ajuster » cadre sur elle plutôt que sur toute la page. */
  emprise?: { x0: number; y0: number; x1: number; y1: number } | null;
}

const ZMIN = 0.02; const ZMAX = 8;

export const PlanCalque = forwardRef<PlanCalquePoignee, Props>(function PlanCalque(p, ref) {
  const cadre = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const [taille, setTaille] = useState<{ l: number; h: number } | null>(null);
  const [k, setK] = useState(1);
  const [erreur, setErreur] = useState<string | null>(null);
  const [curseur, setCurseur] = useState<Pt | null>(null);
  const centreVise = useRef<{ x: number; y: number } | null>(null);
  const doigts = useRef(new Map<number, { x: number; y: number }>());
  const pincement = useRef<{ d: number; k: number } | null>(null);
  const aPince = useRef(false);

  useEffect(() => { setErreur(null); setTaille(null); }, [p.plan.id]);

  const changerZoom = useCallback((nk: number, centre?: { x: number; y: number }) => {
    const c = cadre.current;
    if (c) centreVise.current = centre ?? { x: (c.scrollLeft + c.clientWidth / 2) / k, y: (c.scrollTop + c.clientHeight / 2) / k };
    setK(Math.min(ZMAX, Math.max(ZMIN, nk)));
  }, [k]);
  useLayoutEffect(() => {
    const c = cadre.current; const m = centreVise.current;
    if (!c || !m) return;
    c.scrollLeft = m.x * k - c.clientWidth / 2; c.scrollTop = m.y * k - c.clientHeight / 2;
    centreVise.current = null;
  }, [k]);

  const ajuster = useCallback(() => {
    if (!taille || !cadre.current) return;
    const c = cadre.current;
    const e = p.emprise && p.emprise.x1 > p.emprise.x0 && p.emprise.y1 > p.emprise.y0 ? p.emprise : { x0: 0, y0: 0, x1: taille.l, y1: taille.h };
    const marge = 0.06 * Math.max(e.x1 - e.x0, e.y1 - e.y0);
    const l = e.x1 - e.x0 + 2 * marge, h = e.y1 - e.y0 + 2 * marge;
    const nk = Math.min(ZMAX, Math.max(ZMIN, Math.min((c.clientWidth - 2) / l, (c.clientHeight - 2) / h)));
    const m = { x: (e.x0 + e.x1) / 2, y: (e.y0 + e.y1) / 2 };
    if (nk === k) { c.scrollLeft = m.x * k - c.clientWidth / 2; c.scrollTop = m.y * k - c.clientHeight / 2; }
    else { centreVise.current = m; setK(nk); }
  }, [taille, p.emprise, k]);
  useEffect(() => { if (taille) ajuster(); }, [taille, p.plan.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({ zoomer: (f) => changerZoom(k * f), ajuster }), [changerZoom, k, ajuster]);

  // Centrage sur un point demandé (zoom au moins « lisible »), une fois le fond chargé.
  useEffect(() => {
    if (!p.cible || !taille || !cadre.current) return;
    const c = cadre.current;
    const lisible = Math.max(k, Math.min(ZMAX, Math.min(c.clientWidth, c.clientHeight) / 900));
    centreVise.current = { x: p.cible.x, y: p.cible.y };
    if (lisible !== k) setK(lisible);
    else { c.scrollLeft = p.cible.x * k - c.clientWidth / 2; c.scrollTop = p.cible.y * k - c.clientHeight / 2; centreVise.current = null; }
  }, [p.cible?.jeton, taille]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const c = cadre.current;
    if (!c) return;
    const roue = (e: WheelEvent) => { if (!e.ctrlKey) return; e.preventDefault(); changerZoom(k * (e.deltaY < 0 ? 1.15 : 1 / 1.15)); };
    c.addEventListener('wheel', roue, { passive: false });
    return () => c.removeEventListener('wheel', roue);
  }, [changerZoom, k]);

  // Pincement à deux doigts (le défilement à un doigt reste natif : touch-action pan-x pan-y).
  const doigtPose = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'touch') return;
    doigts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (doigts.current.size === 2) {
      const [a, b] = [...doigts.current.values()];
      pincement.current = { d: Math.hypot(a.x - b.x, a.y - b.y), k };
      aPince.current = true;
    }
  };
  const doigtBouge = (e: PointerEvent<HTMLDivElement>) => {
    if (!doigts.current.has(e.pointerId)) return;
    doigts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pi = pincement.current;
    if (pi && doigts.current.size === 2 && cadre.current) {
      const [a, b] = [...doigts.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const r = cadre.current.getBoundingClientRect();
      const mx = (a.x + b.x) / 2 - r.left, my = (a.y + b.y) / 2 - r.top;
      const centre = { x: (cadre.current.scrollLeft + mx) / k, y: (cadre.current.scrollTop + my) / k };
      const nk = Math.min(ZMAX, Math.max(ZMIN, pi.k * (d / Math.max(1, pi.d))));
      if (Math.abs(nk - k) / k > 0.02) {
        // Garde le point situé sous les doigts au même endroit de l'écran.
        centreVise.current = { x: centre.x - (mx - cadre.current.clientWidth / 2) / nk, y: centre.y - (my - cadre.current.clientHeight / 2) / nk };
        setK(nk);
      }
    }
  };
  const doigtLeve = (e: PointerEvent<HTMLDivElement>) => {
    doigts.current.delete(e.pointerId);
    if (doigts.current.size < 2) pincement.current = null;
    if (!doigts.current.size) window.setTimeout(() => { aPince.current = false; }, 50);
  };

  const versPlan = (cx: number, cy: number): Pt => {
    const r = svg.current!.getBoundingClientRect();
    const x = ((cx - r.left) / r.width) * taille!.l; const y = ((cy - r.top) / r.height) * taille!.h;
    return [Math.round(Math.min(taille!.l, Math.max(0, x)) * 100) / 100, Math.round(Math.min(taille!.h, Math.max(0, y)) * 100) / 100];
  };
  const u = 1 / k;
  const clic = (e: MouseEvent<SVGSVGElement>) => {
    if (!taille || !p.poser || aPince.current) return;
    p.poser(versPlan(e.clientX, e.clientY), u);
  };
  const touche = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!taille || !p.poser || e.target !== e.currentTarget) return;
    const c = curseur ?? [taille.l / 2, taille.h / 2];
    const pas = (e.shiftKey ? 50 : 5) / k;
    const d: Record<string, Pt> = { ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas] };
    if (d[e.key]) { e.preventDefault(); setCurseur([Math.min(taille.l, Math.max(0, c[0] + d[e.key][0])), Math.min(taille.h, Math.max(0, c[1] + d[e.key][1]))]); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); p.poser([Math.round(c[0] * 100) / 100, Math.round(c[1] * 100) / 100], u); }
  };

  return (
    <div className={`prp-plan ${p.compact ? 'prp-plan-compact' : ''}`}>
      <div className="prp-plan-outils" role="toolbar" aria-label="Zoom du plan">
        <Bouton className="btn-icone" icone={<ZoomOut aria-hidden />} onClick={() => changerZoom(k / 1.25)} aria-label="Zoom arrière" />
        <span className="tres-petit chiffre prp-zoom-valeur" aria-live="polite">{Math.round(k * 100)} %</span>
        <Bouton className="btn-icone" icone={<ZoomIn aria-hidden />} onClick={() => changerZoom(k * 1.25)} aria-label="Zoom avant" />
        <Bouton className="btn-icone" icone={<Maximize2 aria-hidden />} onClick={ajuster} aria-label="Ajuster le plan à la fenêtre" />
      </div>
      {p.surimpression && <div className="prp-plan-etiquette">{p.surimpression}</div>}
      <div className={`cch-plan-cadre prp-plan-cadre ${p.poser ? 'prp-plan-trace' : ''}`} ref={cadre}
        onPointerDown={doigtPose} onPointerMove={doigtBouge} onPointerUp={doigtLeve} onPointerCancel={doigtLeve}>
        {erreur && <p className="cch-plan-erreur" role="alert">{erreur}</p>}
        <div className="cch-plan-feuille" style={taille ? { width: taille.l * k, height: taille.h * k } : undefined}>
          <FondPlan url={p.plan.fichier} mime={p.plan.piece?.mime ?? 'image/png'} page={p.plan.page} zoom={k} surTaille={setTaille} surErreur={setErreur} />
          {taille && (
            <svg ref={svg} className="cch-calque prp-calque" viewBox={`0 0 ${taille.l} ${taille.h}`} width={taille.l * k} height={taille.h * k}
              onClick={clic} tabIndex={p.poser ? 0 : -1} onKeyDown={touche} onBlur={() => setCurseur(null)}
              onFocus={() => { if (p.poser) setCurseur((c) => c ?? [taille.l / 2, taille.h / 2]); }}
              role={p.poser ? 'application' : 'img'} aria-roledescription="plan"
              aria-label={p.poser ? `${p.libelle} : flèches pour déplacer le curseur, Entrée pour poser un point, ou touchez le plan.` : p.libelle}>
              {p.calque(u)}
              {curseur && p.poser && (
                <g className="cch-curseur" aria-hidden>
                  <line x1={curseur[0] - 14 * u} y1={curseur[1]} x2={curseur[0] + 14 * u} y2={curseur[1]} vectorEffect="non-scaling-stroke" />
                  <line x1={curseur[0]} y1={curseur[1] - 14 * u} x2={curseur[0]} y2={curseur[1] + 14 * u} vectorEffect="non-scaling-stroke" />
                </g>
              )}
            </svg>
          )}
        </div>
      </div>
    </div>
  );
});
